/**
 * `<caos-empty-state>` — what to show when there is nothing yet; never a bare blank
 * region. (Catalog: Data display › EmptyState — what to show when there is nothing yet; never a bare blank region.)
 *
 * A centred column: an icon (the `icon` slot, defaulting to a neutral inbox glyph),
 * a `title` (attribute), a description (the default slot), and an optional `action`
 * slot for the one thing to do next. Muted, quiet, token-driven. Styling seams:
 * `::part(container)`, `::part(icon)`, `::part(title)`, `::part(description)`,
 * `::part(actions)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

const sheet = makeSheet(`
  :host { display: block; }

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--caos-space-3);
    padding: var(--caos-space-7) var(--caos-space-5);
    max-width: 420px;
    margin: 0 auto;
  }

  .icon {
    width: 40px;
    height: 40px;
    color: var(--caos-color-text-muted);
    opacity: 0.7;
  }
  .icon svg { display: block; width: 100%; height: 100%; }
  ::slotted([slot="icon"]) { width: 40px; height: 40px; color: var(--caos-color-text-muted); }

  .title {
    font-family: var(--caos-font-display);
    font-weight: 600;
    font-size: 16px;
    line-height: 1.3;
    color: var(--caos-color-text);
  }
  .title:empty { display: none; }

  .description {
    font-size: 13px;
    line-height: 1.55;
    color: var(--caos-color-text-muted);
  }

  .actions { display: flex; gap: var(--caos-space-2); margin-top: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }
`);

export class CaosEmptyState extends CaosElement {
  static get observedAttributes(): string[] {
    return ['title'];
  }

  private iconWrap: HTMLSpanElement;
  private defaultIcon: SVGSVGElement;
  private titleEl: HTMLDivElement;

  constructor() {
    super(sheet);

    const container = document.createElement('div');
    container.className = 'container';
    container.setAttribute('part', 'container');

    this.iconWrap = document.createElement('span');
    this.iconWrap.className = 'icon';
    this.iconWrap.setAttribute('part', 'icon');
    this.iconWrap.setAttribute('aria-hidden', 'true');
    // The neutral default; overridable via the `icon` slot. CAOS-602 — this was the vocabulary's
    // `inbox` re-drawn here, path for path. The lighter stroke was the only real difference and it
    // stays: at the size an empty state draws its mark, the standard weight reads as heavy. Setting
    // it on what the vocabulary hands back is a presentation decision, which is what this file gets
    // to make; drawing the glyph again was not.
    this.defaultIcon = makeIcon('inbox');
    this.defaultIcon.setAttribute('stroke-width', '1.75');
    const iconSlot = document.createElement('slot');
    iconSlot.name = 'icon';
    iconSlot.append(this.defaultIcon);
    this.iconWrap.append(iconSlot);

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');

    const description = document.createElement('div');
    description.className = 'description';
    description.setAttribute('part', 'description');
    description.append(document.createElement('slot'));

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    const actionSlot = document.createElement('slot');
    actionSlot.name = 'action';
    actions.append(actionSlot);

    container.append(this.iconWrap, this.titleEl, description, actions);
    this.root.append(container);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    this.titleEl.textContent = this.getAttribute('title') ?? '';
  }
}

define('caos-empty-state', CaosEmptyState);

declare global {
  interface HTMLElementTagNameMap {
    'caos-empty-state': CaosEmptyState;
  }
}
